import React, { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import { createErrorCopyAction } from "./ui/Toaster";
import { usePositions } from "../hooks/usePositions";
import { useFiClient } from "../hooks/useFiClient";
import { useMarkets } from "../hooks/useMarkets";
import { useSubscription } from "../context/SubscriptionContext";
import { Position, SubscriptionClient } from "../sdk";
import {
  formatPrice,
  formatSize,
  formatMargin,
  formatPnL,
  formatLeverage,
} from "../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  LayoutHeader,
  TableHeaderCell,
  TableHeaderCellStatic,
  CloseButton,
} from "./smartlayout/fragments";
import {
  DEFAULT_BASE_URL,
  DEFAULT_HEIGHT_BUCKET_POSITIONS,
  DEFAULT_REFRESH_INTERVAL,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
  ORDER_SIDE_LONG,
  ORDER_SIDE_SHORT,
  MARGIN_MODE_CROSS,
  POSITION_SORT_FIELD_SYMBOL,
  POSITION_SORT_FIELD_LEVERAGE,
  POSITION_SORT_FIELD_SIZE,
  POSITION_SORT_FIELD_LIQUIDATION_PRICE,
  POSITION_SORT_FIELD_UNREALIZED_PNL,
  POSITION_SORT_FIELD_ENTRY_PRICE,
  POSITION_SORT_FIELD_MARGIN,
  POSITION_VIEW_MODE_ALL,
  POSITION_VIEW_MODE_BY_BUCKET,
  POSITION_GRID_TEMPLATE_COLUMNS,
  POSITION_PNL_COLOR_POSITIVE,
  POSITION_PNL_COLOR_NEGATIVE,
  POSITION_LOADING_MESSAGE,
  POSITION_EMPTY_MESSAGE,
  POSITION_TITLE_PREFIX,
  POSITION_TAB_LABEL_ALL,
  POSITION_TAB_LABEL_BY_BUCKET,
  POSITION_LEVERAGE_FALLBACK,
  PositionSortField,
  PositionSortDirection,
  PositionViewMode,
} from "../constants";
import "./smartlayout/layout.css";

interface BucketPositionsProps {
  baseURL?: string;
  /** Optional: when inside SubscriptionProvider, hook uses context. */
  subscriptionClient?: SubscriptionClient;
  authToken?: string;
  walletAddress?: string;
  mnemonic?: string; // Required for closing positions
  height?: string;
  refreshInterval?: number;
  selectedBucketId?: string | null;
  /** When true, do not render LayoutHeader (e.g. when used inside TabSection). */
  hide_header?: boolean;
  /** When hide_header is true, call with the header actions node so parent can show them in a shared toolbar. */
  onRequestHeaderActions?: (node: React.ReactNode) => void;
}

function BucketPositions({
  baseURL = DEFAULT_BASE_URL,
  subscriptionClient: subscriptionClientProp,
  authToken,
  walletAddress,
  mnemonic,
  height = DEFAULT_HEIGHT_BUCKET_POSITIONS,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
  selectedBucketId = null,
  hide_header = false,
  onRequestHeaderActions,
}: BucketPositionsProps): JSX.Element {
  const { client: contextClient } = useSubscription();
  const subscriptionClient = subscriptionClientProp ?? contextClient;

  const {
    positions,
    loading,
    error,
    isConnected,
    refresh: refreshPositions,
  } = usePositions({
    ...(subscriptionClient != null && { subscriptionClient }),
    baseURL,
    authToken,
    walletAddress,
    refreshInterval,
  });
  const { getSymbolByMarketId } = useMarkets({
    baseURL,
    sessionToken: authToken ?? "",
    autoFetchMarkets: true,
  });
  const { client: exchangeClient, isReady: exchangeReady, error: exchangeError } = useFiClient({
    baseURL,
    mnemonic: mnemonic ?? "",
  });

  useEffect(() => {
    if (exchangeError) {
      const msg = exchangeError.message;
      console.error("Failed to initialize FiClient:", exchangeError);
      toast.error("Exchange client initialization failed", {
        description: msg,
        action: createErrorCopyAction(msg),
      });
    }
  }, [exchangeError]);

  const [viewMode, setViewMode] = useState<PositionViewMode>(
    POSITION_VIEW_MODE_ALL,
  );
  const [sortField, setSortField] = useState<PositionSortField | null>(null);
  const [sortDirection, setSortDirection] =
    useState<PositionSortDirection>(SORT_DIRECTION_DESC);

  // Filter and sort positions
  const processedPositions = useMemo(() => {
    let filtered = [...positions];

    // Filter by selected bucket only when view mode is BY_BUCKET
    if (viewMode === POSITION_VIEW_MODE_BY_BUCKET && selectedBucketId) {
      filtered = filtered.filter((pos) => {
        // Convert both to string for comparison to handle number/string mismatches
        const posBucketId = pos.bucketId.toString();
        const selectedId = selectedBucketId.toString();
        return posBucketId === selectedId;
      });
    }

    // Apply sorting only if a column header was clicked (sortField is set)
    if (!sortField) {
      return filtered; // Return filtered positions without sorting
    }

    // Sort by the selected field
    return filtered.sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortField) {
        case POSITION_SORT_FIELD_SYMBOL:
          aValue = getSymbolByMarketId(a.marketId) ?? a.symbol ?? "";
          bValue = getSymbolByMarketId(b.marketId) ?? b.symbol ?? "";
          break;
        case POSITION_SORT_FIELD_LEVERAGE:
          // Convert leverage from satoshi to decimal for proper sorting
          try {
            aValue = a.leverage ?? 0;
            bValue = b.leverage ?? 0;
          } catch {
            aValue = 0;
            bValue = 0;
          }
          break;
        case POSITION_SORT_FIELD_SIZE:
          aValue = a.size || 0;
          bValue = b.size || 0;
          break;
        case POSITION_SORT_FIELD_LIQUIDATION_PRICE:
          aValue = a.liquidationPrice || 0;
          bValue = b.liquidationPrice || 0;
          break;
        case POSITION_SORT_FIELD_UNREALIZED_PNL:
          aValue = a.unrealizedPnL || 0;
          bValue = b.unrealizedPnL || 0;
          break;
        case POSITION_SORT_FIELD_ENTRY_PRICE:
          aValue = a.entryPrice || 0;
          bValue = b.entryPrice || 0;
          break;
        case POSITION_SORT_FIELD_MARGIN:
          aValue = a.margin || 0;
          bValue = b.margin || 0;
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === SORT_DIRECTION_ASC
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = Number(aValue);
      const numB = Number(bValue);

      return sortDirection === SORT_DIRECTION_ASC ? numA - numB : numB - numA;
    });
  }, [positions, sortField, sortDirection, selectedBucketId, viewMode, getSymbolByMarketId]);

  const handleSort = (field: PositionSortField) => {
    if (sortField === field) {
      setSortDirection(
        sortDirection === SORT_DIRECTION_ASC
          ? SORT_DIRECTION_DESC
          : SORT_DIRECTION_ASC,
      );
    } else {
      setSortField(field);
      setSortDirection(SORT_DIRECTION_DESC);
    }
  };

  // Ref to store the loading toast ID so we can dismiss it later
  const loadingToastIdRef = useRef<number>(0);

  const handleClosePosition = async (position: Position) => {
    // Check if mnemonic is available
    if (!mnemonic) {
      const mnemonicErrorMsg =
        "Please provide the 'mnemonic' prop to enable position closing.";
      toast.error("Mnemonic required", {
        description: mnemonicErrorMsg,
        action: createErrorCopyAction(mnemonicErrorMsg),
      });
      return;
    }

    // Check if FiClient is initialized
    if (!exchangeClient || !exchangeReady) {
      const clientNotReadyMsg =
        "Please wait for the exchange client to initialize.";
      toast.error("Exchange client not ready", {
        description: clientNotReadyMsg,
        action: createErrorCopyAction(clientNotReadyMsg),
      });
      return;
    }

    // Show entry toast (when closing starts)
    const displaySymbol = getSymbolByMarketId(position.marketId) ?? position.symbol;
    const toastId = toast.loading("Closing position...", {
      description: `Closing ${position.side} position for ${displaySymbol} (Size: ${formatSize(position.size)})`,
    });
    loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;

    try {
      // Close the position using FiClient
      const response = await exchangeClient.closePosition({
        positionId: position.positionId,
        marketId: position.marketId,
        marketPrice: position.markPrice, // Required: current mark price for the position
        quantity: position.size, // Optional: pass to close partial position, omit to close entire position
        bucketId: position.bucketId, // Shard hint for multi-node routing (E2E alignment)
      });

      // Dismiss entry toast and show success toast
      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }
      toast.success("Position closed successfully", {
        description: `${position.side} position for ${displaySymbol} has been closed. tx: ${response.txhash}`,
        duration: 5000,
      });

      // Refresh positions to reflect the change
      if (refreshPositions) {
        await refreshPositions();
      }
    } catch (error) {
      // Dismiss entry toast and show error toast
      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      toast.error("Failed to close position", {
        description: errorMessage,
        duration: 6000,
        action: createErrorCopyAction(errorMessage),
      });
      console.error("Failed to close position:", error);
    }
  };

  const headerActions = useMemo(
    () => (
      <>
        <div
          onClick={() => {
            setViewMode(POSITION_VIEW_MODE_ALL);
            setSortField(null);
          }}
          className={`btn-tab ${viewMode === POSITION_VIEW_MODE_ALL ? "active" : ""}`}
        >
          {POSITION_TAB_LABEL_ALL}
        </div>
        <div
          onClick={() => {
            setViewMode(POSITION_VIEW_MODE_BY_BUCKET);
            setSortField(null);
          }}
          className={`btn-tab ${viewMode === POSITION_VIEW_MODE_BY_BUCKET ? "active" : ""}`}
        >
          <img src="/bucket-g.svg" />
          <span>{POSITION_TAB_LABEL_BY_BUCKET}</span>
        </div>
        {!isConnected && (
          <div
            onClick={async () => {
              if (refreshPositions) {
                await refreshPositions();
              }
            }}
            className="btn-tab"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            title="WebSocket disconnected - Click to refresh positions"
          >
            <img
              src="/caution-icy.svg"
              alt="Warning"
              style={{ width: "1rem", height: "1rem" }}
            />
            <span>Refresh</span>
          </div>
        )}
      </>
    ),
    [viewMode, isConnected, refreshPositions],
  );

  useEffect(() => {
    if (!onRequestHeaderActions) return;
    if (hide_header && !loading && !error) {
      onRequestHeaderActions(headerActions);
      return () => onRequestHeaderActions(null);
    }
    onRequestHeaderActions(null);
  }, [hide_header, onRequestHeaderActions, loading, error, headerActions]);

  if (loading) {
    return <LoadingMessage message={POSITION_LOADING_MESSAGE} />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div
      className="layout-container"
      style={{
        height,
        ...(hide_header
          ? { borderRadius: 0, background: "transparent" }
          : {}),
      }}
    >
      {!hide_header && (
        <LayoutHeader
          title={`${POSITION_TITLE_PREFIX} ${viewMode === POSITION_VIEW_MODE_BY_BUCKET && selectedBucketId ? `(Bucket ${selectedBucketId})` : ""}`}
        >
          {headerActions}
        </LayoutHeader>
      )}

      {/* Table */}
      <div className="table-container">
        {/* Table Header */}
        <div
          className="table-header"
          style={{
            gridTemplateColumns: POSITION_GRID_TEMPLATE_COLUMNS,
          }}
        >
          <TableHeaderCell
            field={POSITION_SORT_FIELD_SYMBOL}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            left
          >
            Symbol
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_LEVERAGE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Leverage
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_SIZE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Size
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_LIQUIDATION_PRICE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Liq. Price
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_UNREALIZED_PNL}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            P/L
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_ENTRY_PRICE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Entry Price
          </TableHeaderCell>
          <TableHeaderCell
            field={POSITION_SORT_FIELD_MARGIN}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Margin
          </TableHeaderCell>
          <TableHeaderCellStatic center>Actions</TableHeaderCellStatic>
        </div>

        {/* Table Body */}
        {processedPositions.length === 0 ? (
          <EmptyMessage message={POSITION_EMPTY_MESSAGE} />
        ) : (
          processedPositions.map((position: Position, index: number) => {
            const isLong = position.side === ORDER_SIDE_LONG;
            const pnlColor =
              (position.unrealizedPnL || 0) >= 0
                ? POSITION_PNL_COLOR_POSITIVE
                : POSITION_PNL_COLOR_NEGATIVE;
            const leverageDisplay = formatLeverage(
              position.leverage,
              POSITION_LEVERAGE_FALLBACK,
            );

            return (
              <div
                key={`position-${position.marketId}-${index}`}
                className={`table-row ${index % 2 === 0 ? "even" : ""}`}
                style={{
                  gridTemplateColumns: POSITION_GRID_TEMPLATE_COLUMNS,
                }}
              >
                {/* Symbol */}
                <div
                  className="table-cell primary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span>{getSymbolByMarketId(position.marketId) ?? position.symbol}</span>
                </div>

                {/* Leverage */}
                <div className="color-buy" style={{ fontWeight: 500 }}>
                { ` ${isLong ? ORDER_SIDE_LONG : ORDER_SIDE_SHORT}  ${leverageDisplay === POSITION_LEVERAGE_FALLBACK ? POSITION_LEVERAGE_FALLBACK :leverageDisplay}x @ ${position.marginMode || MARGIN_MODE_CROSS}`}
                </div>

                {/* Size */}
                <div className="color-buy right monospace">
                  {formatSize(position.size)}
                </div>

                {/* Liq. Price */}
                <div className="table-cell right monospace">
                  {formatPrice(position.liquidationPrice)}
                </div>

                {/* P/L */}
                <div
                  className="table-cell right monospace"
                  style={{ color: pnlColor, fontWeight: 500 }}
                >
                  {formatPnL(position.unrealizedPnL)}
                </div>

                {/* Entry Price */}
                <div className="table-cell right monospace">
                  {formatPrice(position.entryPrice)}
                </div>

                {/* Margin */}
                <div className="table-cell right monospace">
                  {formatMargin(position.margin)}
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CloseButton onClick={() => handleClosePosition(position)} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default React.memo(BucketPositions);
